/* eslint-disable import/no-unresolved */
/* eslint-disable no-shadow */
import React, {
	RefAttributes,
	memo,
	useCallback,
	useState,
	useMemo,
	useEffect,
} from "react";
import { react, useRefWithSetter, Button, theme } from "uikit";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

import { PaymentSystem, Language } from "../../../../../../../services";
import { PaymentProvider } from "../../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../../hooks/useObjectEditor";
import EditModalBase from "../../../../../../../components/EditModal";
import { StyledRow, StyledSpan } from "../../../../../../../components/common";

import { Content, Root, Header, ActivateModal } from "./components";
import InternalController from "./Controller";

const ModalBase = react.withController<Modal.PropsBase, Modal.Controller>(
	({
		value,
		onCancel,
		onSave,
		createdAt,
		headerTitle,
		controller,
		loading,
		errorTextAll,
		setErrorTextAll,
	}) => {
		const { t } = useTranslation();
		const [contentRef, setContentRef] =
			useRefWithSetter<Content.Ref | null>(null);

		const [internalValue, setInternalValue] = useState(value);
		const [activateModal, setActivateModal] = useState<boolean>(false);

		const valueEditor = useObjectEditor(internalValue, setInternalValue);

		const contentOnChange = valueEditor.useAssigner();

		const paymentProvider = valueEditor.useGetter("paymentProvider");

		const [assetCheck, setAssetCheck] = [
			valueEditor.useGetter("assetCheck"),
			valueEditor.useSetter("assetCheck"),
		];

		const [assetAction, setAssetAction] = [
			valueEditor.useGetter("assetAction"),
			valueEditor.useSetter("assetAction"),
		];

		const [maibCheck, setMaibCheck] = [
			valueEditor.useGetter("maibCheck"),
			valueEditor.useSetter("maibCheck"),
		];

		const [maibAction, setMaibAction] = [
			valueEditor.useGetter("maibAction"),
			valueEditor.useSetter("maibAction"),
		];

		const [
			assetPaymentsKey,
			assetPaymentsSecretKey,
			assetPaymentsProcessingId,
			assetPaymentsCurrency,
			assetPaymentsCountryIso,
		] = [
			valueEditor.useGetter("assetPaymentsKey"),
			valueEditor.useGetter("assetPaymentsSecretKey"),
			valueEditor.useGetter("assetPaymentsProcessingId"),
			valueEditor.useGetter("assetPaymentsCurrency"),
			valueEditor.useGetter("assetPaymentsCountryIso"),
		];

		const [
			maibPaymentsKey,
			maibPaymentsSecretKey,
			maibSignatureId,
			maibPaymentsCurrency,
			maibPaymentsCountryIso,
		] = [
			valueEditor.useGetter("maibPaymentsKey"),
			valueEditor.useGetter("maibPaymentsSecretKey"),
			valueEditor.useGetter("maibSignatureId"),
			valueEditor.useGetter("maibPaymentsCurrency"),
			valueEditor.useGetter("maibPaymentsCountryIso"),
		];

		const assetCurrentValue: PaymentSystem.AssetPaymentConfigurationPayload =
			useMemo(
				() => ({
					assetPaymentsKey,
					assetPaymentsSecretKey,
					assetPaymentsProcessingId,
					assetPaymentsCurrency,
					assetPaymentsCountryIso,
				}),
				[
					assetPaymentsCountryIso,
					assetPaymentsCurrency,
					assetPaymentsKey,
					assetPaymentsProcessingId,
					assetPaymentsSecretKey,
				],
			);

		const maibCurrentValue: PaymentSystem.MaibPaymentConfigurationPayload =
			useMemo(
				() => ({
					maibPaymentsKey,
					maibPaymentsSecretKey,
					maibSignatureId,
					maibPaymentsCurrency,
					maibPaymentsCountryIso,
				}),
				[
					maibPaymentsCountryIso,
					maibPaymentsCurrency,
					maibPaymentsKey,
					maibPaymentsSecretKey,
					maibSignatureId,
				],
			);

		const [assetPreConnectedValue, setAssetPreConnectedValue] =
			useState<PaymentSystem.AssetPaymentConfigurationPayload>(
				assetCurrentValue,
			);
		const [maibPreConnectedValue, setMaibPreConnectedValue] =
			useState<PaymentSystem.MaibPaymentConfigurationPayload>(
				maibCurrentValue,
			);

		const handleSubmit = useCallback(() => {
			if (!contentRef.current?.validate()) {
				return;
			}

			onSave(internalValue as PaymentSystem.Modified);
		}, [contentRef, internalValue, onSave]);

		const [allCorectFormForAsset, setAllCorectFormForAsset] =
			useState<boolean>(false);
		const [allCorectFormForMaid, setAllCorectFormForMaid] =
			useState<boolean>(false);

		useEffect(() => {
			const timer = setTimeout(() => {
				if (paymentProvider === PaymentProvider.ASSETPAYMENT) {
					const isAssetPaymentsKey = !!assetPaymentsKey.trim();
					const isAssetPaymentsSecretKey =
						!!assetPaymentsSecretKey.trim();
					const isAssetPaymentsProcessingId =
						!!assetPaymentsProcessingId;

					const validEmpty = [
						isAssetPaymentsKey,
						isAssetPaymentsSecretKey,
						isAssetPaymentsProcessingId,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						assetPreConnectedValue,
						assetCurrentValue,
					);

					setAllCorectFormForAsset(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (assetCheck !== false) setAssetCheck(false);
						// if (assetAction !== false) setAssetAction(false);
					}
				}
				if (paymentProvider === PaymentProvider.MAIB) {
					const isMaibPaymentsKey = !!maibPaymentsKey.trim();
					const isMaibPaymentsSecretKey =
						!!maibPaymentsSecretKey.trim();
					const isMaibSignatureId = !!maibSignatureId.trim();

					const validEmpty = [
						isMaibPaymentsKey,
						isMaibPaymentsSecretKey,
						isMaibSignatureId,
					].every((isValid) => isValid);

					const validDifferent = isEqual(
						maibPreConnectedValue,
						maibCurrentValue,
					);

					setAllCorectFormForMaid(validEmpty);
					if (!validEmpty || !validDifferent) {
						if (maibCheck !== false) setMaibCheck(false);
						// if (maibAction !== false) setMaibAction(false);
					}
				}
			}, 0);

			// 	paymentProvider,
			// assetPaymentsKey,
			// assetPaymentsSecretKey,
			// assetPaymentsProcessingId,
			// maibPaymentsKey,
			// maibPaymentsSecretKey,
			// maibSignatureId,

			return () => clearTimeout(timer);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			paymentProvider,
			assetPaymentsKey,
			assetPaymentsSecretKey,
			assetPaymentsProcessingId,
			assetCurrentValue,
			maibPaymentsKey,
			maibPaymentsSecretKey,
			maibSignatureId,
			maibCurrentValue,
		]);

		const connectPayment = useCallback(async () => {
			if (paymentProvider === PaymentProvider.ASSETPAYMENT) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setAssetCheck(isConnect);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setAssetPreConnectedValue(internalValue);

					setAssetPreConnectedValue((prev) => ({
						...prev,
						...assetCurrentValue,
					}));
				}
			}
			if (paymentProvider === PaymentProvider.MAIB) {
				const { result, error } = await PaymentSystem.connectCheck(
					internalValue,
				);

				const isConnect = !!result;

				setMaibCheck(!!result);
				setErrorTextAll(error ?? "");

				if (isConnect) {
					setMaibPreConnectedValue(internalValue);

					setMaibPreConnectedValue((prev) => ({
						...prev,
						...maibCurrentValue,
					}));
				}
			}
		}, [
			assetCurrentValue,
			internalValue,
			maibCurrentValue,
			paymentProvider,
			setAssetCheck,
			setErrorTextAll,
			setMaibCheck,
		]);

		const activatePayment = useCallback(() => {
			setActivateModal(false);

			if (paymentProvider === PaymentProvider.ASSETPAYMENT) {
				setAssetAction(true);
				setMaibAction(false);
			}
			if (paymentProvider === PaymentProvider.MAIB) {
				setMaibAction(true);
				setAssetAction(false);
			}
		}, [paymentProvider, setAssetAction, setMaibAction]);

		const disabledBtnConect: boolean = useMemo(
			() =>
				(paymentProvider === PaymentProvider.ASSETPAYMENT &&
					(!allCorectFormForAsset || assetCheck)) ||
				(paymentProvider === PaymentProvider.MAIB &&
					(!allCorectFormForMaid || maibCheck)) ||
				![PaymentProvider.MAIB, PaymentProvider.ASSETPAYMENT].includes(
					paymentProvider,
				),
			[
				allCorectFormForAsset,
				allCorectFormForMaid,
				assetCheck,
				maibCheck,
				paymentProvider,
			],
		);

		const disabledBtnActivate: boolean = useMemo(
			() =>
				(paymentProvider === PaymentProvider.ASSETPAYMENT &&
					(!assetCheck || assetAction)) ||
				(paymentProvider === PaymentProvider.MAIB &&
					(!maibCheck || maibAction)) ||
				![PaymentProvider.MAIB, PaymentProvider.ASSETPAYMENT].includes(
					paymentProvider,
				),
			[assetAction, assetCheck, maibAction, maibCheck, paymentProvider],
		);

		const textTranslate = useMemo(() => {
			const connectText =
				t("settings.paymentSystems.modal.btns.connect") ?? "";
			const activateText =
				t("settings.paymentSystems.modal.btns.activate") ?? "";

			const isChecked =
				(paymentProvider === PaymentProvider.ASSETPAYMENT &&
					assetCheck) ||
				(paymentProvider === PaymentProvider.MAIB && maibCheck);

			const isActivate =
				(paymentProvider === PaymentProvider.ASSETPAYMENT &&
					assetAction) ||
				(paymentProvider === PaymentProvider.MAIB && maibAction);

			return {
				connect: isChecked ? `✅ ${connectText}` : connectText,
				activate: isActivate ? `✅ ${activateText}` : activateText,
			};
		}, [
			t,
			paymentProvider,
			assetCheck,
			maibCheck,
			assetAction,
			maibAction,
		]);

		controller.setContext({ contentRef });

		return (
			<EditModalBase
				onCancel={onCancel}
				onSave={handleSubmit}
				loading={loading}
				footer={
					<StyledRow gap="16px">
						<Button.Button
							style={{ padding: "9px 25px", height: "36px" }}
							text={textTranslate.connect}
							onClick={connectPayment}
							disabled={disabledBtnConect}
						/>

						<Button.Button
							style={{ padding: "9px 25px", height: "36px" }}
							text={textTranslate.activate}
							onClick={() => setActivateModal(true)}
							disabled={disabledBtnActivate}
						/>
					</StyledRow>
				}
			>
				<Root sizes="auto! 1fr">
					<Header title={headerTitle} createdAt={createdAt} />
					<Content
						ref={setContentRef}
						value={internalValue}
						onChange={contentOnChange}
					/>
					<StyledRow p="20px 24px">
						<StyledSpan colors={theme.colors.error}>
							{errorTextAll}
						</StyledSpan>
					</StyledRow>
					{activateModal && (
						<ActivateModal
							onClose={() => setActivateModal(false)}
							onSubmit={activatePayment}
							paymentProviderValue={paymentProvider}
						/>
					)}
				</Root>
			</EditModalBase>
		);
	},
	InternalController,
);
const Modal = memo(ModalBase);

declare namespace Modal {
	type Ref = InternalController | null;
	type Controller = InternalController;

	interface PropsBase {
		value: Value;
		headerTitle: string;
		createdAt: string;
		lang: Language;
		onCancel: () => void;
		loading?: boolean;
		onSave: (template: PaymentSystem.Modified) => Promise<void>;
		errorTextAll: string;
		setErrorTextAll: (err: string) => void;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	type Value = PaymentSystem.Model;
	type PreparedTemplate = PaymentSystem.New | PaymentSystem.Modified | null;
}

export default Modal;
